import { enqueueSnackbar } from 'notistack'
import { axiosInstance } from './axiosInstance'
import { AxiosResponse } from 'axios'
import {
    GlobalSubscriptions,
    SubscriptionResponse,
} from '../components/UserGroups/interfaces'
import i18n from '../i18n'

// Utility functions

const handleResponse = <T>(
    response: AxiosResponse<T>,
    successMessage?: string,
): T => {
    if (response.status >= 200 && response.status < 300) {
        if (response && successMessage) {
            enqueueSnackbar(successMessage, { variant: 'success' })
        }
        return response.data
    } else {
        throw new Error('Error')
    }
}

const handleError = (err: any, errorMessage?: string) => {
    const message =
        errorMessage || err.response?.data?.message || 'An error occurred'
    enqueueSnackbar(message, { variant: 'error' })
    // throw new Error(message)
}

export const makeRequest = async <T>(
    method: 'get' | 'post' | 'put' | 'delete',
    url: string,
    data?: unknown,
    params?: unknown,
    successMessage?: string,
    errorMessage?: string,
): Promise<T> => {
    try {
        const response: AxiosResponse<T> = await axiosInstance({
            method,
            url,
            data,
            params,
        })
        return handleResponse(response, successMessage)
    } catch (err) {
        handleError(err, errorMessage)
        return undefined as unknown as T
    }
}

export const fetchGroups = async (
    params: unknown,
    orgId: string,
): Promise<any> => {
    const response: any = await makeRequest(
        'get',
        `/groups?orgId=${orgId}`,
        null,
        params,
    )
    return response
}

export const addGroup = async (
    data: unknown,
    orgId: string,
): Promise<unknown> => {
    return await makeRequest(
        'post',
        `/groups`,
        data,
        {
            orgId,
        },
        i18n.t('userGroupCreateSuccess'),
        i18n.t('userGroupCreateError'),
    )
}

export const updateGroup = async (
    payload: unknown,
    orgId: string,
): Promise<void> => {
    await makeRequest<void>(
        'put',
        `/groups`,
        payload,
        {
            orgId,
        },
        i18n.t('userGroupUpdateSuccess'),
        i18n.t('userGroupUpdateError'),
    )
}

export const deleteGroup = async (
    groups: unknown,
    orgId: string | undefined,
): Promise<void> => {
    await makeRequest<any>(
        'delete',
        `/groups`,
        { groups },
        {
            orgId,
        },
        i18n.t('userGroupDeleteSuccess'),
        i18n.t('userGroupDeleteError'),
    )
}

export const fetchUsersByGruopID = async (
    params: unknown,
    groupId: string | undefined,
): Promise<any> => {
    const response: any = await makeRequest(
        'get',
        `/groups/userGroup/${groupId}`,
        null,
        params,
    )
    return response
}

export const updateUserInGroup = async (
    payload: unknown,
    org: { id: string },
): Promise<void> => {
    await makeRequest<void>(
        'put',
        `/groups/userGroup`,
        payload,
        {
            orgId: org.id,
        },
        i18n.t('userUpdateSuccess'),
        i18n.t('userUpdateError'),
    )
}

export const deleteUserInGroup = async (
    members: unknown,
    orgId: string,
): Promise<void> => {
    await makeRequest<any>(
        'delete',
        `/groups/userGroup`,
        members,
        {
            orgId,
        },
        i18n.t('userDeleteSuccess'),
        i18n.t('userDeleteError'),
    )
}

export const inviteUserInGroup = async (
    data: unknown,
    orgId: string,
): Promise<unknown> => {
    return await makeRequest(
        'post',
        `/groups/userGroup`,
        data,
        {
            orgId,
        },
        i18n.t('invitationSentSuccess'),
        i18n.t('invitationSentError'),
    )
}

export const getSubscriptions = async (
    userGroupId: string,
    orgId: string,
): Promise<SubscriptionResponse> => {
    const response = await makeRequest<SubscriptionResponse>(
        'get',
        `groups/userGroup/subscriptions`,
        null,
        {
            userGroupId,
            orgId,
        },
    )
    return response
}

export const updateSubscriptions = async (
    userGroupId: string,
    globalSubscriptions: GlobalSubscriptions,
): Promise<void> => {
    await makeRequest<void>(
        'post',
        `/groups/subscriptions`,
        {
            globalSubscriptions,
        },
        {
            userGroupId,
        },
        i18n.t('subscriptionsUpdateSuccess'),
        i18n.t('subscriptionsUpdateError'),
    )
}

export const fetchGroupDataById = async (
    groupId: string,
    orgId: string,
): Promise<any> => {
    const response: any = await makeRequest(
        'get',
        `/groups/${groupId}?orgId=${orgId}`,
    )
    return response
}
